import { useCallback, useRef, useEffect } from 'react';
import type { Ref } from 'react';
import logger from '@/utils/client-logger';

type PossibleRef<T> = Ref<T> | undefined;

export function assignRef<T>(ref: PossibleRef<T>, value: T) {
  if (ref == null) return;

  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  try {
    (ref as React.MutableRefObject<T>).current = value;
  } catch (error) {
    logger.log(error);
    throw new Error(`Cannot assign value '${value}' to ref '${ref}'`);
  }
}

export function mergeRefs<T>(...refs: Array<PossibleRef<T>>) {
  return (node: T | null) => {
    refs.forEach((ref) => {
      assignRef(ref, node);
    });
  };
}

/**
 * 合并多个 ref 的 Hook
 *
 * React 社区推荐的最佳实践，用于将多个 ref 合并到一个元素上
 *
 * 支持以下类型的 ref：
 * - RefObject<T>
 * - RefCallback<T>
 * - null
 * - undefined
 *
 * @param refs 要合并的 ref（可变参数）
 * @returns 合并后的 ref 回调函数
 *
 * @example
 * ```tsx
 * const ref1 = useRef<HTMLDivElement>(null);
 * const { ref: ref2 } = useInView();
 * const { ref: ref3 } = useValidExposure({ callback: () => {} });
 *
 * const mergedRef = useMergedRefs(ref1, ref2, ref3);
 * return <div ref={mergedRef}>Content</div>;
 * ```
 */
export function useMergedRefs<T>(...refs: Array<PossibleRef<T>>) {
  // 使用 useRef 保存最新的 refs 数组，避免依赖数组问题
  const refsRef = useRef(refs);

  // 每次渲染时更新 refs
  useEffect(() => {
    refsRef.current = refs;
  });

  // 使用 useCallback 返回稳定的回调函数
  return useCallback(
    (node: T | null) => {
      refsRef.current.forEach((ref) => {
        assignRef(ref, node);
      });
    },
    [], // 空依赖数组，因为我们使用 refsRef.current 来获取最新的 refs
  );
}
